import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { ApiError } from '../../lib/api';
import { rooms, type Review } from './api';
import { Textarea } from '../../components/ui/form';

const STARS = [1, 2, 3, 4, 5];

/**
 * The host's review of what is playing
 * (dev/design/GetVibed DJ Console.dc.html).
 *
 * ## The quietly valuable thing on this screen
 *
 * An artist who gets played on a room learns a number. A sentence from the
 * person who chose it is worth more than the play count, and until this there
 * was nowhere in the product for that sentence to go.
 *
 * ## Two buttons, because a host needs somewhere to be honest
 *
 * "Send" notifies the artist. "Save private" is a crate note - *good, but the
 * mix is thin, ask again in six months* - that they never see. Collapsing them
 * into one would not make hosts kinder; it would make them write nothing, and
 * the private half is what makes the sent half worth reading.
 *
 * Once sent, it stays sent. The server accepts an edit but will not unsend, and
 * the panel says so rather than offering a control that quietly does nothing.
 */
export function ReviewPanel({ trackId, onClose }: { trackId: number; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [rating, setRating] = useState<number | null>(null);
  const [body, setBody] = useState('');

  const { data } = useQuery({
    queryKey: ['station-review', trackId],
    queryFn: () => rooms.review(trackId),
  });

  const existing = data?.review ?? null;

  /*
   * Seeded once the server answers, and keyed on the track so switching to a
   * different one does not leave the last one's words in the box. Deliberately
   * not a controlled mirror of `existing` - a host typing while the refetch
   * lands would watch their sentence revert.
   */
  useEffect(() => {
    setRating(existing?.rating ?? null);
    setBody(existing?.body ?? '');
  }, [existing?.id, trackId]);

  const save = useMutation({
    mutationFn: (visibility: Review['visibility']) =>
      rooms.saveReview(trackId, { rating, body: body.trim(), visibility }),
    onSuccess: (result) => {
      queryClient.setQueryData(['station-review', trackId], result);
    },
  });

  const sent = existing?.visibility === 'sent';
  const empty = rating === null && body.trim() === '';

  return (
    <div
      data-review-panel
      className="flex flex-col gap-3.5 rounded-[var(--radius-panel)] border border-line-strong bg-deep p-4"
    >
      <div className="flex items-center justify-between gap-3">
        <span className="meta-caps text-faint">
          {sent ? 'Host review · sent to the artist' : 'Host review · private'}
        </span>
        <button
          type="button"
          onClick={onClose}
          className="font-mono text-[11px] text-soft transition-colors hover:text-ink"
        >
          close
        </button>
      </div>

      <div className="flex gap-2" role="radiogroup" aria-label="Rating">
        {STARS.map((star) => (
          <button
            key={star}
            type="button"
            role="radio"
            aria-checked={rating === star}
            data-star={star}
            // Clicking the current rating clears it: a host who meant four and
            // hit five needs a way back to no rating at all, and there is
            // nowhere else to put it.
            onClick={() => setRating(rating === star ? null : star)}
            className={`h-9 w-9 rounded-[var(--radius-control)] border text-[13px] transition-colors ${
              rating !== null && star <= rating
                ? 'border-transparent bg-accent text-accent-ink'
                : 'border-line text-soft hover:border-line-strong hover:text-ink'
            }`}
          >
            {star}
          </button>
        ))}
      </div>

      <Textarea
        value={body}
        maxLength={1000}
        data-review-body
        placeholder="what worked, what to try next…"
        onChange={(event) => setBody(event.target.value)}
        className="min-h-[70px] resize-y rounded-[var(--radius-control)] border border-line bg-app px-3 py-2.5 text-[13px] text-ink placeholder:text-faint focus:border-line-strong"
      />

      {save.isError && (
        <Alert tone="error">
          {save.error instanceof ApiError ? save.error.message : 'That could not be saved.'}
        </Alert>
      )}

      <div className="flex items-center gap-2.5">
        <Button
          full
          disabled={empty || save.isPending}
          loading={save.isPending && save.variables === 'sent'}
          onClick={() => save.mutate('sent')}
          data-review-send
        >
          {sent ? 'Update' : 'Send review'}
        </Button>
        {!sent && (
          <Button
            variant="secondary"
            disabled={empty || save.isPending}
            onClick={() => save.mutate('private')}
            data-review-private
          >
            Save private
          </Button>
        )}
      </div>

      <p className="font-mono text-[10px] text-ghost">
        {sent
          ? 'They have seen this. Editing changes what they read; it cannot be unsent.'
          : 'Private stays yours. Sending notifies the artist.'}
      </p>
    </div>
  );
}
